import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { dmY } from "@/lib/dateFmt";
import TxnDateCell from "@/components/TxnDateCell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, HourglassMedium, PencilSimple } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";
import ExportButton from "@/components/ExportButton";
import SortHeader, { sortRows } from "@/components/SortHeader";
import PageShortcutHeader from "@/components/PageShortcutHeader";
import { sortPaymentModes } from "@/lib/paymentModes";
import { sanitizeUtr, isValidUtr, UTR_FORMAT_ERROR } from "@/lib/utr";
import { Funnel, CaretDown, CaretUp, X } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

const emptyForm = {
    party_id: "", date: new Date().toISOString().slice(0, 10),
    txn_type: "invoice", reference: "", description: "", amount: "", utr: "", payment_mode: "",
};

const TXN_LABELS = {
    invoice: "Invoice",
    payment: "Payment Received",
    commission: "Commission",
    payout: "Payment Made",
    adjustment: "Adjustment",
    portal_fee: "Portal Fee",
};

const labelForType = (t) =>
    TXN_LABELS[t] || String(t || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// v1.29.16 — Empty draft for the advanced filter panel. Kept in a plain
// object so we can shallow-diff pending vs applied and drive the badge
// count off a single source of truth. `types` is a plain array because
// arrays serialise cleanly into URLSearchParams; the checkbox toggle
// helper below keeps it deduped.
const EMPTY_ADV_FILTERS = {
    types: [],
    refSearch: "",
    minAmount: "",
    maxAmount: "",
    entryType: "all", // "all" | "debit" | "credit"
};

const countActiveFilters = (f) => {
    let n = 0;
    if (f.types && f.types.length) n += 1;
    if ((f.refSearch || "").trim()) n += 1;
    if (String(f.minAmount).trim() !== "") n += 1;
    if (String(f.maxAmount).trim() !== "") n += 1;
    if (f.entryType && f.entryType !== "all") n += 1;
    return n;
};

export default function Transactions() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [txns, setTxns] = useState([]);
    const [sort, setSort] = useState({ by: "date", dir: "desc" });
    const [pendingUploads, setPendingUploads] = useState([]);
    const [parties, setParties] = useState([]);
    const [ledger, setLedger] = useState(null);
    const [filterParty, setFilterParty] = useState(searchParams.get("party_id") || "all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [paymentModes, setPaymentModes] = useState([]);
    // v1.29.20 — Always render Payment Modes A-Z case-insensitive with
    // Title-Case display + "RECIVED" typo corrected. Uses the shared helper
    // so every dropdown across the app has identical behaviour.
    const sortedPaymentModes = useMemo(() => sortPaymentModes(paymentModes), [paymentModes]);
    const [newModeName, setNewModeName] = useState("");
    // v1.29.16 — Advanced filter state. `pending` = what the user is
    // editing in the collapsible panel; `applied` = the criteria actually
    // driving the table + summary + export. Two-state so quick keystrokes
    // don't recompute filters on every character — user commits with the
    // Apply button (or Enter inside a text input).
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [pendingFilters, setPendingFilters] = useState(EMPTY_ADV_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_ADV_FILTERS);
    const activeFilterCount = useMemo(() => countActiveFilters(appliedFilters), [appliedFilters]);

    const loadModes = useCallback(async () => {
        try {
            const { data } = await api.get("/payment-modes");
            setPaymentModes(data);
        } catch (_e) { /* ignore */ }
    }, []);

    const addMode = async () => {
        const name = newModeName.trim();
        if (!name) return;
        try {
            const { data } = await api.post("/payment-modes", { name });
            setPaymentModes((prev) => {
                const exists = prev.some((m) => (m.name || "").toLowerCase() === name.toLowerCase());
                return exists ? prev : [{ ...data, usage_count: 0 }, ...prev];
            });
            setForm((f) => ({ ...f, payment_mode: data.name || name }));
            setNewModeName("");
            toast.success(`Added "${data.name || name}"`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { loadModes(); }, [loadModes]);

    // Keep URL in sync so browser back/forward and shared links work
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        if (filterParty === "all") params.delete("party_id"); else params.set("party_id", filterParty);
        setSearchParams(params, { replace: true });
    }, [filterParty]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async () => {
        const params = {};
        if (filterParty !== "all") params.party_id = filterParty;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const [r1, r2] = await Promise.all([
            api.get("/transactions", { params }),
            api.get("/parties"),
        ]);
        setTxns(r1.data);
        setParties(r2.data);

        // If a party filter is active, also fetch their pending uploads AND ledger totals
        if (filterParty !== "all") {
            const ledgerParams = {};
            if (dateFrom) ledgerParams.date_from = dateFrom;
            if (dateTo) ledgerParams.date_to = dateTo;
            const [up, led] = await Promise.all([
                api.get("/uploads", { params: { status: "pending" } }),
                api.get(`/ledger/${filterParty}`, { params: ledgerParams }).catch(() => ({ data: null })),
            ]);
            setPendingUploads(up.data.filter((u) => u.party_id === filterParty));
            setLedger(led.data);
        } else {
            setPendingUploads([]);
            setLedger(null);
        }
    }, [filterParty, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    const partyName = useMemo(() => {
        const m = new Map(parties.map((p) => [p.id, p.name]));
        return (id) => m.get(id) || "—";
    }, [parties]);

    const save = async (e, override = false) => {
        e?.preventDefault?.();
        const utrVal = (form.utr || "").trim();
        if (utrVal && !isValidUtr(utrVal)) { toast.error(UTR_FORMAT_ERROR); return; }
        try {
            // v1.29.28 — override_duplicate re-posts after the admin confirms
            // "Post anyway" on the 409 duplicate prompt.
            await api.post("/transactions", { ...form, amount: Number(form.amount), override_duplicate: override });
            toast.success("Transaction added · party stays selected for the next entry");
            // v1.28.44 — Sticky party after save. Only reset the entry-specific
            // fields (amount, reference, description, utr, payment_mode) so the
            // user can post multiple consecutive entries for the same party
            // without reselecting it every time. Date and txn_type are also
            // preserved because they typically repeat within a session. The
            // dialog stays open; the user closes it manually when done.
            setForm((prev) => ({
                ...emptyForm,
                party_id: prev.party_id,
                date: prev.date,
                txn_type: prev.txn_type,
            }));
            load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (override !== true && e?.response?.status === 409 && detail?.code === "DUPLICATE_PAYMENT") {
                if (window.confirm(`${detail.message}\n\nPost anyway?`)) return save(null, true);
                return;
            }
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete transaction?")) return;
        await api.delete(`/transactions/${id}`);
        toast.success("Deleted");
        load();
    };

    const [editOpen, setEditOpen] = useState(false);
    const [editTxn, setEditTxn] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const openEdit = (t) => {
        setEditTxn(t);
        setEditForm({
            date: t.date || "",
            amount: t.amount ?? "",
            reference: t.reference ?? "",
            description: t.description ?? "",
            payment_mode: t.payment_mode ?? "",
            charge_percent: t.charge_percent ?? "",
            commission_percent: "",
            commission_amount: "",
            portal_fee_amount: "",
        });
        setEditOpen(true);
    };
    const saveEdit = async (override = false) => {
        if (!editTxn) return;
        const tt = editTxn.txn_type;
        const patch = { override_duplicate: override };
        if (editForm.date) patch.date = editForm.date;
        if (String(editForm.amount) !== "") patch.amount = Number(editForm.amount);
        patch.reference = editForm.reference;
        patch.description = editForm.description;
        if (tt === "payout") patch.payment_mode = editForm.payment_mode;
        if (tt === "payment") {
            if (String(editForm.commission_percent) !== "") patch.commission_percent = Number(editForm.commission_percent);
            if (String(editForm.commission_amount) !== "") patch.commission_amount = Number(editForm.commission_amount);
            if (String(editForm.portal_fee_amount) !== "") patch.portal_fee_amount = Number(editForm.portal_fee_amount);
        }
        if (tt === "commission" && String(editForm.charge_percent) !== "") patch.charge_percent = Number(editForm.charge_percent);
        try {
            setEditSaving(true);
            await api.patch(`/transactions/${editTxn.id}`, patch);
            toast.success("Entry updated");
            setEditOpen(false); setEditTxn(null);
            load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (override !== true && e?.response?.status === 409 && detail?.code === "DUPLICATE_PAYMENT") {
                setEditSaving(false);
                if (window.confirm(`${detail.message}\n\nSave anyway?`)) return saveEdit(true);
                return;
            }
            toast.error(formatApiError(e));
        } finally { setEditSaving(false); }
    };

    const clearFilters = () => { setDateFrom(""); setDateTo(""); setFilterParty("all"); setPendingFilters(EMPTY_ADV_FILTERS); setAppliedFilters(EMPTY_ADV_FILTERS); };

    // v1.29.16 — Dynamic Transaction Type list: seed with the six known
    // labels then merge in any custom txn_type actually present in the
    // loaded data (future-proof against new categories introduced later
    // without a code change).
    const availableTypes = useMemo(() => {
        const seen = new Set(Object.keys(TXN_LABELS));
        for (const t of txns) if (t.txn_type) seen.add(t.txn_type);
        return Array.from(seen).sort((a, b) => labelForType(a).localeCompare(labelForType(b)));
    }, [txns]);

    // v1.29.16 — Client-side application of the advanced filter criteria.
    // Party / From / To already scoped the backend query above; this pass
    // adds Type / Reference / Amount Range / Debit-Credit on top, giving
    // the user instant feedback without a round trip. Export params below
    // reuse the exact same criteria so the downloaded file matches what
    // the user sees on screen.
    const filteredTxns = useMemo(() => {
        if (activeFilterCount === 0) return txns;
        const min = String(appliedFilters.minAmount).trim();
        const max = String(appliedFilters.maxAmount).trim();
        const minVal = min === "" ? null : Number(min);
        const maxVal = max === "" ? null : Number(max);
        const q = (appliedFilters.refSearch || "").trim().toLowerCase();
        const typeSet = appliedFilters.types.length ? new Set(appliedFilters.types) : null;
        return txns.filter((t) => {
            if (typeSet && !typeSet.has(t.txn_type)) return false;
            if (q) {
                const hay = `${t.reference || ""} ${t.description || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            const debit = t.amount_signed > 0 ? t.amount_signed : 0;
            const credit = t.amount_signed < 0 ? -t.amount_signed : 0;
            if (appliedFilters.entryType === "debit" && debit <= 0) return false;
            if (appliedFilters.entryType === "credit" && credit <= 0) return false;
            if (minVal !== null || maxVal !== null) {
                const magnitude = Math.max(debit, credit); // use the visible amount, not signed
                if (minVal !== null && magnitude < minVal) return false;
                if (maxVal !== null && magnitude > maxVal) return false;
            }
            return true;
        });
    }, [txns, appliedFilters, activeFilterCount]);

    // v1.29.16 — Filtered totals for the sticky summary bar. Uses the
    // amount_signed convention (positive = debit, negative = credit) so
    // Net = Credit − Debit reads as "money owed to the party" in the
    // exact same lens as the Balance Snapshot cards above the table.
    const filteredTotals = useMemo(() => {
        let debit = 0, credit = 0;
        for (const t of filteredTxns) {
            const s = Number(t.amount_signed || 0);
            if (s > 0) debit += s; else credit += -s;
        }
        return {
            count: filteredTxns.length,
            debit: Math.round(debit * 100) / 100,
            credit: Math.round(credit * 100) / 100,
            net: Math.round((credit - debit) * 100) / 100,
        };
    }, [filteredTxns]);

    // v1.29.16 — Export params always mirror what the user sees. Party +
    // date scope already goes in; add the advanced filters as CSV / plain
    // scalars so the backend export can filter identically. Empty values
    // are omitted so the URL stays clean and the backend defaults kick in.
    const exportParams = useMemo(() => {
        const p = {};
        if (filterParty !== "all") p.party_id = filterParty;
        if (dateFrom) p.date_from = dateFrom;
        if (dateTo) p.date_to = dateTo;
        if (appliedFilters.types.length) p.txn_types = appliedFilters.types.join(",");
        if ((appliedFilters.refSearch || "").trim()) p.ref_search = appliedFilters.refSearch.trim();
        if (String(appliedFilters.minAmount).trim() !== "") p.min_amount = String(appliedFilters.minAmount).trim();
        if (String(appliedFilters.maxAmount).trim() !== "") p.max_amount = String(appliedFilters.maxAmount).trim();
        if (appliedFilters.entryType && appliedFilters.entryType !== "all") p.entry_type = appliedFilters.entryType;
        return p;
    }, [filterParty, dateFrom, dateTo, appliedFilters]);

    const applyAdvFilters = () => { setAppliedFilters(pendingFilters); setFiltersOpen(false); };
    const clearAdvFilters = () => { setPendingFilters(EMPTY_ADV_FILTERS); setAppliedFilters(EMPTY_ADV_FILTERS); };
    const toggleType = (t) => {
        setPendingFilters((prev) => {
            const has = prev.types.includes(t);
            return { ...prev, types: has ? prev.types.filter((x) => x !== t) : [...prev.types, t] };
        });
    };

    // v1.29.14 — Running balance per transaction, computed chronologically
    // regardless of the user's display sort. Starts from ledger.opening_balance
    // (which the backend already scopes to "balance strictly BEFORE date_from"
    // when a From date is set — see server.py v1.28.15 comment) and adds each
    // amount_signed in date order. Ties on `date` break on `created_at` so two
    // entries on the same day still stack correctly. The final chrono row's
    // balance MUST match ledger.closing_balance; if not we console.warn so a
    // stale opening or missing signed amount surfaces immediately.
    const runningBalances = useMemo(() => {
        const map = new Map();
        if (!ledger?.party || !txns.length) return map;
        const opening = Number(ledger.opening_balance || 0);
        const chrono = [...txns].sort((a, b) => {
            const da = new Date(a.date || 0).getTime();
            const db = new Date(b.date || 0).getTime();
            if (da !== db) return da - db;
            const ca = new Date(a.created_at || 0).getTime();
            const cb = new Date(b.created_at || 0).getTime();
            return ca - cb;
        });
        let bal = opening;
        for (const t of chrono) {
            bal += Number(t.amount_signed || 0);
            map.set(t.id, Math.round(bal * 100) / 100);
        }
        // Reconcile with backend-computed closing balance. Tolerance keeps
        // rounding noise from firing a false alarm on 30+ entry ledgers.
        const expected = Number(ledger.closing_balance || 0);
        if (Math.abs(bal - expected) > 0.5) {
            // eslint-disable-next-line no-console
            console.warn(
                `[Party Ledger] Running balance ₹${bal.toFixed(2)} for ${ledger.party.name} ` +
                `does not match closing balance ₹${expected.toFixed(2)} · drift ₹${(bal - expected).toFixed(2)}`
            );
        }
        return map;
    }, [txns, ledger]);

    return (
        <div className="space-y-6" data-testid="transactions-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <PageShortcutHeader
                    action_id="page:transactions"
                    label="Transactions"
                    target_route="/admin/transactions"
                    eyebrow="Ledger"
                    title="Transactions"
                    titleClassName="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900"
                />
                <div className="flex items-center gap-2">
                    <ExportButton
                        endpoint={{ pdf: "/export/transactions.pdf", xlsx: "/export/transactions.xlsx" }}
                        params={exportParams}
                        filenameBase="transactions"
                        testId="txn-export"
                    />
                    <Dialog open={open} onOpenChange={(v) => {
                        // v1.28.47 — When opening the dialog, if the form's
                        // party is blank, seed it from the currently applied
                        // party filter (e.g. user was viewing "Fancy Rexine"
                        // → New Entry pre-selects Fancy Rexine).
                        if (v) {
                            setForm((prev) => (prev.party_id ? prev : {
                                ...prev,
                                party_id: filterParty !== "all" ? filterParty : "",
                            }));
                        }
                        setOpen(v);
                    }}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-txn-btn" className="bg-blue-600 text-white hover:bg-blue-700">
                            <Plus size={16} className="mr-2" /> New Entry
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                        <DialogHeader>
                            <DialogTitle className="font-display">Collection Entry</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={save} className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Party *</Label>
                                <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                                    <SelectTrigger data-testid="txn-party" className="mt-2"><SelectValue placeholder="Select party" /></SelectTrigger>
                                    <SelectContent>{parties.filter((p) => p.active !== false).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <F label="Date *" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="txn-date" />
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Type *</Label>
                                <Select value={form.txn_type} onValueChange={(v) => setForm({ ...form, txn_type: v })}>
                                    <SelectTrigger data-testid="txn-type" className="mt-2"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="invoice">Invoice (Debit)</SelectItem>
                                        <SelectItem value="payment">Payment Received (Credit)</SelectItem>
                                        <SelectItem value="payout">Payment Made (Debit)</SelectItem>
                                        <SelectItem value="adjustment">Adjustment</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <F label="Reference" value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} testId="txn-ref" />
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">UTR</Label>
                                <Input data-testid="txn-utr" inputMode="numeric" maxLength={12} value={form.utr ?? ""} onChange={(e) => setForm({ ...form, utr: sanitizeUtr(e.target.value) })} className="mt-2" />
                                {form.utr && !isValidUtr(form.utr) && (
                                    <p data-testid="txn-utr-error" className="mt-1 text-[11px] font-medium text-rose-600">{UTR_FORMAT_ERROR}</p>
                                )}
                            </div>
                            <F label="Amount *" type="number" step="0.01" required value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testId="txn-amount" />
                            {form.txn_type === "payout" && (
                                <div className="col-span-2 rounded-md border border-violet-200 bg-violet-50/40 p-3">
                                    <Label className="text-xs uppercase tracking-[0.2em] text-violet-800">Payment Mode</Label>
                                    <div className="mt-1.5 flex items-center gap-2">
                                        <Select value={form.payment_mode || ""} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                                            <SelectTrigger data-testid="txn-payment-mode" className="flex-1 bg-white"><SelectValue placeholder="Select mode" /></SelectTrigger>
                                            <SelectContent>
                                                {paymentModes.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No modes yet — add one below</div>}
                                                {sortedPaymentModes.map((m) => (
                                                    <SelectItem key={m.id || m.name} value={m.name}>
                                                        {m.name} {m.usage_count > 0 && <span className="ml-1 text-[10px] text-slate-400">({m.usage_count})</span>}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="mt-2 flex items-center gap-2">
                                        <Input
                                            data-testid="txn-new-payment-mode"
                                            value={newModeName}
                                            onChange={(e) => setNewModeName(e.target.value)}
                                            placeholder="Or add new mode (e.g. Bill Payment)"
                                            className="bg-white text-sm"
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMode(); } }}
                                        />
                                        <Button type="button" onClick={addMode} disabled={!newModeName.trim()} data-testid="txn-add-payment-mode-btn" variant="outline" size="sm">
                                            <Plus size={14} className="mr-1" /> Add
                                        </Button>
                                    </div>
                                    <div className="mt-1 text-[10px] text-violet-700/70">Dropdown is ordered by usage — most-used modes float to the top.</div>
                                </div>
                            )}
                            <div className="col-span-2">
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Description</Label>
                                <Input data-testid="txn-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2" />
                            </div>
                            <DialogFooter className="col-span-2">
                                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                                <Button type="submit" data-testid="save-txn-btn" className="bg-blue-600 text-white hover:bg-blue-700">Save Entry</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {/* Filters */}
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Party</Label>
                        <Select value={filterParty} onValueChange={setFilterParty}>
                            <SelectTrigger data-testid="filter-party" className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All parties</SelectItem>
                                {parties.filter((p) => p.active !== false).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">From</Label>
                        <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">To</Label>
                        <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" />
                    </div>
                    <div className="flex items-end">
                        <Button data-testid="clear-filters-btn" onClick={clearFilters} variant="outline" className="w-full">Clear</Button>
                    </div>
                </div>

                {/* v1.29.16 — Advanced filters (collapsible). Sits under Party/From/To
                    so the existing date scope combines with these, never replaces it. */}
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { if (!filtersOpen) setPendingFilters(appliedFilters); setFiltersOpen((v) => !v); }}
                        data-testid="advanced-filters-toggle"
                        className="inline-flex items-center gap-2"
                    >
                        <Funnel size={14} weight="duotone" />
                        <span>Advanced filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}</span>
                        {activeFilterCount > 0 && (
                            <span data-testid="advanced-filters-badge" className="inline-flex items-center justify-center rounded-full bg-blue-600 px-1.5 py-0 text-[10px] font-bold leading-none text-white">
                                {activeFilterCount}
                            </span>
                        )}
                        {filtersOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}
                    </Button>
                    {activeFilterCount > 0 && (
                        <button
                            type="button"
                            data-testid="advanced-filters-clear-inline"
                            onClick={clearAdvFilters}
                            className="text-xs font-semibold uppercase tracking-wider text-rose-600 underline underline-offset-2 hover:text-rose-700"
                        >
                            Clear all filters
                        </button>
                    )}
                </div>

                {filtersOpen && (
                    <div className="mt-3 space-y-4 rounded-md border border-slate-200 bg-slate-50/60 p-4" data-testid="advanced-filters-panel">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Transaction Type</Label>
                            <div className="mt-2 flex flex-wrap gap-2" data-testid="advanced-filters-types">
                                {availableTypes.map((t) => {
                                    const active = pendingFilters.types.includes(t);
                                    return (
                                        <button
                                            key={t}
                                            type="button"
                                            data-testid={`filter-type-${t}`}
                                            onClick={() => toggleType(t)}
                                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition ${active ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"}`}
                                        >
                                            <span className={`inline-block h-2 w-2 rounded-full ${active ? "bg-white" : "bg-slate-300"}`} />
                                            {labelForType(t)}
                                        </button>
                                    );
                                })}
                                {availableTypes.length === 0 && <span className="text-xs text-slate-400">No types available yet.</span>}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Reference / Description</Label>
                                <Input
                                    data-testid="filter-ref-search"
                                    value={pendingFilters.refSearch}
                                    onChange={(e) => setPendingFilters((p) => ({ ...p, refSearch: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyAdvFilters(); } }}
                                    placeholder="e.g. Goodluck, UTR12345…"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Min Amount (₹)</Label>
                                <Input
                                    data-testid="filter-min-amount"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={pendingFilters.minAmount}
                                    onChange={(e) => setPendingFilters((p) => ({ ...p, minAmount: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyAdvFilters(); } }}
                                    placeholder="0"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Max Amount (₹)</Label>
                                <Input
                                    data-testid="filter-max-amount"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={pendingFilters.maxAmount}
                                    onChange={(e) => setPendingFilters((p) => ({ ...p, maxAmount: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyAdvFilters(); } }}
                                    placeholder="Any"
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Entry Type</Label>
                            <div className="mt-2 flex flex-wrap gap-4" data-testid="advanced-filters-entry-type">
                                {[
                                    { v: "all", label: "All" },
                                    { v: "debit", label: "Debit only" },
                                    { v: "credit", label: "Credit only" },
                                ].map((opt) => (
                                    <label key={opt.v} className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                                        <input
                                            type="radio"
                                            data-testid={`filter-entry-type-${opt.v}`}
                                            name="adv-entry-type"
                                            checked={pendingFilters.entryType === opt.v}
                                            onChange={() => setPendingFilters((p) => ({ ...p, entryType: opt.v }))}
                                            className="h-4 w-4 accent-green-600"
                                        />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 pt-3">
                            <button
                                type="button"
                                data-testid="advanced-filters-clear"
                                onClick={() => setPendingFilters(EMPTY_ADV_FILTERS)}
                                className="text-xs font-semibold uppercase tracking-wider text-slate-500 underline underline-offset-2 hover:text-slate-800"
                            >
                                Clear all filters
                            </button>
                            <Button
                                type="button"
                                data-testid="advanced-filters-apply"
                                onClick={applyAdvFilters}
                                className="bg-blue-600 text-white hover:bg-blue-700"
                                size="sm"
                            >
                                Apply Filters
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Party balance summary — only visible when a specific party is selected */}
            {ledger && ledger.party && (
                <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5 shadow-sm" data-testid="party-balance-card">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-blue-700/70">Balance snapshot</div>
                            <h2 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="party-balance-name">
                                {ledger.party.name}
                            </h2>
                        </div>
                        {ledger.charge_percent > 0 && (
                            <div className="rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-medium text-blue-800">
                                Charge: {ledger.charge_percent}%
                            </div>
                        )}
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <BalanceStat label="Opening" value={fmt(ledger.opening_balance)} testId="stat-opening" />
                        <BalanceStat label="Total Debit" value={fmt(ledger.total_debit ?? 0)} testId="stat-debit" tone="rose" />
                        <BalanceStat label="Total Credit" value={fmt(ledger.total_credit ?? 0)} testId="stat-credit" tone="emerald" />
                        <BalanceStat label="Closing" value={fmt(ledger.closing_balance)} testId="stat-closing" tone={ledger.closing_balance > 0 ? "amber" : "slate"} highlight />
                    </div>
                </div>
            )}

            {/* Slips uploaded in selected date range — shows only when party + at least one date filter is applied */}
            {ledger && ledger.range_stats && (
                <div className="rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm" data-testid="txn-slips-in-range-card">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-xs uppercase tracking-[0.2em] text-blue-700/70">
                            Slips uploaded · {ledger.range_stats.date_from || "…"} → {ledger.range_stats.date_to || "…"}
                        </div>
                        <div className="text-sm text-slate-600">
                            Verified amount: <span className="font-mono font-semibold text-emerald-700">{fmt(ledger.range_stats.verified_amount)}</span>
                        </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <MiniStat label="Total" value={ledger.range_stats.total_count} testId="txn-rs-total" />
                        <MiniStat label="Verified" value={ledger.range_stats.verified_count} tone="emerald" testId="txn-rs-verified" />
                        <MiniStat label="Pending" value={ledger.range_stats.pending_count} tone="amber" testId="txn-rs-pending" />
                        <MiniStat label="Rejected" value={ledger.range_stats.rejected_count} tone="rose" testId="txn-rs-rejected" />
                    </div>
                </div>
            )}

            {/* Pending slips for selected party */}
            {pendingUploads.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 shadow-sm" data-testid="pending-slips-banner">
                    <div className="flex items-center justify-between border-b border-amber-100 px-5 py-3">
                        <div className="flex items-center gap-2">
                            <HourglassMedium size={18} weight="fill" className="text-amber-700" />
                            <div className="font-display text-base font-semibold text-amber-900">
                                {pendingUploads.length} payment slip{pendingUploads.length > 1 ? "s" : ""} awaiting verification
                            </div>
                        </div>
                        <a href={`/admin/uploads?party_id=${filterParty}&status=pending`} className="text-xs uppercase tracking-[0.2em] text-amber-800 underline">
                            Verify now →
                        </a>
                    </div>
                    <ul className="divide-y divide-amber-100">
                        {pendingUploads.map((u) => (
                            <li key={u.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                                <div className="flex items-center gap-3">
                                    <span className="text-slate-700">{dmY(u.payment_date)}</span>
                                    <span className="font-mono text-xs text-slate-500">UTR {u.utr || "—"}</span>
                                    <span className="text-xs text-slate-500">{u.payment_app}</span>
                                </div>
                                <span className="font-medium tabular-nums text-slate-900">{fmt(u.amount)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {txns.length === 0 ? (
                <EmptyState title="No transactions found" description="Try changing filters or add a new entry." />
            ) : (
                <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="txns-table">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                    <th className="px-5 py-3 font-medium"><SortHeader field="date" sort={sort} onChange={setSort}>Date</SortHeader></th>
                                    <th className="px-5 py-3 font-medium">Party</th>
                                    <th className="px-5 py-3 font-medium">Type</th>
                                    <th className="px-5 py-3 font-medium">Reference</th>
                                    <th className="px-5 py-3 text-right font-medium"><SortHeader field={(r) => (r.amount_signed > 0 ? r.amount_signed : 0)} align="right" sort={sort} onChange={setSort}>Debit</SortHeader></th>
                                    <th className="px-5 py-3 text-right font-medium"><SortHeader field={(r) => (r.amount_signed < 0 ? -r.amount_signed : 0)} align="right" sort={sort} onChange={setSort}>Credit</SortHeader></th>
                                    <th className="px-5 py-3 text-right font-medium" data-testid="txns-balance-header">Balance</th>
                                    <th className="px-5 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTxns.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="px-5 py-10 text-center text-sm text-slate-400" data-testid="txns-empty-filtered">
                                            No transactions match the current filters. Try widening the amount range or clearing a type checkbox.
                                        </td>
                                    </tr>
                                ) : sortRows(filteredTxns, sort).map((t) => {
                                    const debit = t.amount_signed > 0 ? t.amount_signed : 0;
                                    const credit = t.amount_signed < 0 ? -t.amount_signed : 0;
                                    const label = TXN_LABELS[t.txn_type] || t.txn_type;
                                    const badgeCls = {
                                        payment: "border-emerald-200 bg-emerald-50 text-emerald-800",
                                        commission: "border-blue-200 bg-blue-50 text-blue-800",
                                        payout: "border-violet-200 bg-violet-50 text-violet-800",
                                        invoice: "border-slate-200 bg-slate-50 text-slate-700",
                                    }[t.txn_type] || "border-slate-200 bg-slate-50 text-slate-700";
                                    return (
                                        <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                                            <td className="px-5 py-3 text-slate-600 align-top">
                                                <TxnDateCell
                                                    date={t.date}
                                                    created_at={t.created_at}
                                                    updated_at={t.updated_at}
                                                    testId={`txn-date-${t.id}`}
                                                />
                                            </td>
                                            <td className="px-5 py-3 text-slate-900">{partyName(t.party_id)}</td>
                                            <td className="px-5 py-3">
                                                <span className={`rounded border px-2 py-0.5 text-xs uppercase tracking-wide ${badgeCls}`}>
                                                    {label}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-slate-600">
                                                {/* v1.27.54 — Single-line reference: the previous two-line layout
                                                    rendered `t.reference || "—"` on the first line and
                                                    `t.description` on the second, so commission / portal-fee
                                                    rows (which have an empty `reference`) showed a lonely
                                                    dash above the real text like:
                                                        —
                                                        VALUE QUICKNPAY RECD received from party
                                                    Now show a single line: use `reference` when present,
                                                    fall back to `description`, and a dash only when both
                                                    are empty. */}
                                                {t.reference || t.description || "—"}
                                            </td>
                                            <td className="px-5 py-3 text-right tabular-nums text-slate-900">{debit ? fmt(debit) : "—"}</td>
                                            <td className="px-5 py-3 text-right tabular-nums text-emerald-700">{credit ? fmt(credit) : "—"}</td>
                                            <td
                                                data-testid={`txn-balance-${t.id}`}
                                                className={`px-5 py-3 text-right tabular-nums font-semibold whitespace-nowrap ${
                                                    !runningBalances.has(t.id)
                                                        ? "text-slate-400"
                                                        : runningBalances.get(t.id) < 0
                                                            ? "text-rose-700"
                                                            : runningBalances.get(t.id) > 0
                                                                ? "text-emerald-700"
                                                                : "text-slate-800"
                                                }`}
                                            >
                                                {runningBalances.has(t.id) ? fmt(runningBalances.get(t.id)) : "—"}
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        data-testid={`edit-txn-${t.id}`}
                                                        onClick={() => openEdit(t)}
                                                        title="Edit entry"
                                                        className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600"
                                                    >
                                                        <PencilSimple size={16} />
                                                    </button>
                                                    <button
                                                        data-testid={`delete-txn-${t.id}`}
                                                        onClick={() => remove(t.id)}
                                                        title="Delete entry"
                                                        className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                                                    >
                                                        <Trash size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {activeFilterCount > 0 && filteredTxns.length > 0 && (
                                <tfoot className="sticky bottom-0 z-10 bg-white">
                                    <tr className="border-t-2 border-slate-300 bg-gradient-to-r from-blue-50 to-emerald-50" data-testid="txns-summary-row">
                                        <td colSpan={4} className="px-5 py-3 text-left align-middle text-xs uppercase tracking-[0.2em] text-slate-700">
                                            <span className="font-semibold">Filtered totals</span>
                                            <span className="ml-2 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="txns-summary-count">
                                                {filteredTotals.count} rows
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-right tabular-nums text-slate-900" data-testid="txns-summary-debit">
                                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Debit</div>
                                            <div className="font-semibold">{fmt(filteredTotals.debit)}</div>
                                        </td>
                                        <td className="px-5 py-3 text-right tabular-nums text-emerald-700" data-testid="txns-summary-credit">
                                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Credit</div>
                                            <div className="font-semibold">{fmt(filteredTotals.credit)}</div>
                                        </td>
                                        <td colSpan={2} className={`px-5 py-3 text-right tabular-nums ${filteredTotals.net >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="txns-summary-net">
                                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Net (Credit − Debit)</div>
                                            <div className="font-bold">{fmt(filteredTotals.net)}</div>
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditTxn(null); }}>
            <DialogContent data-testid="edit-txn-dialog">
                <DialogHeader>
                    <DialogTitle>Edit {editTxn ? (TXN_LABELS[editTxn.txn_type] || editTxn.txn_type) : "Entry"}</DialogTitle>
                </DialogHeader>
                {editTxn && (
                    <div className="space-y-4">
                        <F label="Date" type="date" value={editForm.date} onChange={(v) => setEditForm({ ...editForm, date: v })} testId="edit-txn-date" />
                        <F label="Amount (₹)" type="number" step="0.01" value={editForm.amount} onChange={(v) => setEditForm({ ...editForm, amount: v })} testId="edit-txn-amount" />
                        <F label="Reference / UTR" value={editForm.reference} onChange={(v) => setEditForm({ ...editForm, reference: v })} testId="edit-txn-reference" />
                        <F label="Description / Remark" value={editForm.description} onChange={(v) => setEditForm({ ...editForm, description: v })} testId="edit-txn-description" />
                        {editTxn.txn_type === "payout" && (
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Payment Mode</Label>
                                <Select value={editForm.payment_mode} onValueChange={(v) => setEditForm({ ...editForm, payment_mode: v })}>
                                    <SelectTrigger data-testid="edit-txn-payment-mode" className="mt-2"><SelectValue placeholder="Select mode" /></SelectTrigger>
                                    <SelectContent>{sortedPaymentModes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        )}
                        {editTxn.txn_type === "commission" && (
                            <F label="Commission %" type="number" step="0.01" value={editForm.charge_percent} onChange={(v) => setEditForm({ ...editForm, charge_percent: v })} testId="edit-txn-charge-percent" />
                        )}
                        {editTxn.txn_type === "payment" && (
                            <div className="rounded-md border border-blue-100 bg-blue-50/50 p-3 space-y-3">
                                <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-800">Commission & Portal Fee (optional)</div>
                                <p className="text-[11px] text-slate-500">Fill these to add or correct this payment's commission (the % / % Amt shown in the ledger). Leave blank to keep the current charges. Enter a % or an exact ₹ amount.</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <F label="Commission %" type="number" step="0.01" value={editForm.commission_percent} onChange={(v) => setEditForm({ ...editForm, commission_percent: v })} testId="edit-txn-commission-percent" />
                                    <F label="Commission ₹" type="number" step="0.01" value={editForm.commission_amount} onChange={(v) => setEditForm({ ...editForm, commission_amount: v })} testId="edit-txn-commission-amount" />
                                </div>
                                <F label="Portal Fee ₹" type="number" step="0.01" value={editForm.portal_fee_amount} onChange={(v) => setEditForm({ ...editForm, portal_fee_amount: v })} testId="edit-txn-portal-fee-amount" />
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => { setEditOpen(false); setEditTxn(null); }} data-testid="edit-txn-cancel">Cancel</Button>
                    <Button onClick={() => saveEdit(false)} disabled={editSaving} data-testid="edit-txn-save" className="bg-blue-600 text-white hover:bg-blue-700">{editSaving ? "Saving…" : "Save Changes"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </div>
    );
}

function F({ label, value, onChange, type = "text", step, required, testId }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</Label>
            <Input data-testid={testId} type={type} step={step} required={required} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-2" />
        </div>
    );
}

function MiniStat({ label, value, tone = "slate", testId }) {
    const tones = {
        slate: "text-slate-900",
        emerald: "text-emerald-700",
        amber: "text-amber-700",
        rose: "text-rose-700",
    };
    return (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
            <div className={`mt-0.5 font-display text-lg font-bold tabular-nums ${tones[tone]}`}>{value}</div>
        </div>
    );
}

function BalanceStat({ label, value, testId, tone = "slate", highlight = false }) {
    const tones = {
        slate: "bg-white border-slate-200 text-slate-900",
        rose: "bg-white border-rose-100 text-rose-800",
        emerald: "bg-white border-emerald-100 text-emerald-800",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
    };
    return (
        <div className={`rounded-lg border p-3 shadow-sm ${tones[tone] || tones.slate} ${highlight ? "ring-1 ring-amber-300" : ""}`} data-testid={testId}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
            <div className="mt-1 font-mono text-lg font-semibold tabular-nums">{value}</div>
        </div>
    );
}
